import React, { useState, useCallback } from 'react';
import { Shield, ChevronRight } from 'lucide-react';
import { Pagination, Badge, Loading, EmptyState } from '../../../common';
import {
  getInitials,
  formatType,
  getRiskClassName,
} from '../../../../utils/helpers';
import { TYPE_COLORS } from '../../../../utils/constants';
import './CriticalRiskIdentitiesTable.css';
import formatNumber from '../../../../utils/formatNumber';
/**
 * CriticalRiskIdentitiesTable Component
 * Displays critical risk identities in a styled table with onRowClick support
 */
const CriticalRiskIdentitiesTable = ({
  identities = [],
  loading = false,
  activeTab = 'non-human',
  onTabChange,
  onViewAllIdentities,
  pagination,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  onViewDetails,
  selectedId = null,
}) => {
  /* ── Normalize risk ────────────────────────── */
  const getSafeRisk = useCallback(
    (risk) => ({
      stage: risk?.stage || 'unknown',
      score: risk?.score ?? 0,
    }),
    []
  );
  /* ── Handle row click ──────────────────────── */
  const handleRowClick = useCallback(
    (identity) => {
      if (onRowClick) onRowClick(identity);
    },
    [onRowClick]
  );
  /* ── Handle view details button ─────────────── */
  const handleViewDetails = useCallback(
    (e, identity) => {
      e.stopPropagation();
      if (onViewDetails) onViewDetails(identity);
    },
    [onViewDetails]
  );
  /* ── Conditional rendering (after hooks) ─────── */
  if (loading) {
    return (
      <div className="critical-risk-container">
        <div className="table-header">
          <div className="header-content">
            <h2>Critical Risk Identities</h2>
            <p>Identities requiring immediate attention</p>
          </div>
          <div className="table-header-right">
            {onTabChange && (
              <div className="crit-tabs">
                {['human', 'non-human'].map((type) => (
                  <button
                    key={type}
                    className={`crit-tab-btn ${activeTab === type ? 'active' : ''}`}
                    onClick={() => onTabChange(type)}
                  >
                    {type === 'non-human' ? 'Non-human' : 'Human'}
                  </button>
                ))}
              </div>
            )}
            {onViewAllIdentities && (
              <button className="view-all-identities" onClick={onViewAllIdentities}>
                View all <span className="arrow">›</span>
              </button>
            )}
          </div>
        </div>
        <div className="table-wrapper">
          <p style={{ padding: '1rem' }}>Loading critical identities...</p>
        </div>
      </div>
    );
  }
  if (!identities.length) {
    return (
      <div className="critical-risk-container">
        <div className="table-header">
          <div className="header-content">
            <h2>Critical Risk Identities</h2>
            <p>Identities requiring immediate attention</p>
          </div>
          <div className="table-header-right">
            {onTabChange && (
              <div className="crit-tabs">
                {['human', 'non-human'].map((type) => (
                  <button
                    key={type}
                    className={`crit-tab-btn ${activeTab === type ? 'active' : ''}`}
                    onClick={() => onTabChange(type)}
                  >
                    {type === 'non-human' ? 'Non-human' : 'Human'}
                  </button>
                ))}
              </div>
            )}
            {onViewAllIdentities && (
              <button className="view-all-identities" onClick={onViewAllIdentities}>
                View all <span className="arrow">›</span>
              </button>
            )}
          </div>
        </div>
        <div className="table-wrapper">
          <p className="critical-risk-empty-message">No critical identities found.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="critical-risk-container">
      <div className="table-header">
        <div className="header-content">
          <h2>Critical Risk Identities</h2>
          <p>Identities requiring immediate attention</p>
        </div>
        <div className="table-header-right">
          {onTabChange && (
            <div className="crit-tabs">
              {['human', 'non-human'].map((type) => (
                <button
                  key={type}
                  className={`crit-tab-btn ${activeTab === type ? 'active' : ''}`}
                  onClick={() => onTabChange(type)}
                >
                  {type === 'non-human' ? 'Non-human' : 'Human'}
                </button>
              ))}
            </div>
          )}
          {onViewAllIdentities && (
            <button className="view-all-identities" onClick={onViewAllIdentities}>
              View all <span className="arrow">›</span>
            </button>
          )}
        </div>
      </div>
      <div className="critical-risk-table-container">
        <table className="critical-risk-table">
          <thead>
            <tr>
              <th scope="col" className="critical-th-identity">Identity</th>
              <th scope="col" className="critical-th-type">Type</th>
              <th scope="col" className="critical-th-department">Department</th>
              <th scope="col" className="critical-th-status">Status</th>
              <th scope="col" className="critical-th-risk">Risk</th>
              <th scope="col" className="critical-th-score">Score</th>
              <th scope="col" className="critical-th-owner">Owner</th>
            </tr>
          </thead>
          <tbody>
            {identities.map((item) => {
              const risk = getSafeRisk(item?.risk);
              const rowId = item?.id || item?.objectId;
              return (
                <tr
                  key={rowId}
                  className={`critical-risk-row ${selectedId === rowId ? 'critical-risk-row--selected' : ''}`}
                  onClick={() => handleRowClick(item)}
                >
                  {/* ── Identity ──────────────────────── */}
                  <td className="td-identity">
                    <div className="critical-identity-cell">
                      <div
                        className="critical-identity-avatar"
                        style={{
                          backgroundColor: `${TYPE_COLORS[item?.type] || '#999'}15`,
                          color: TYPE_COLORS[item?.type] || '#999',
                        }}
                      >
                        {item?.type === 'Application' || item?.type === 'ServicePrincipal' ? (
                          <Shield size={16} strokeWidth={2.5} />
                        ) : (
                          getInitials(item?.name || item?.displayName || item?.email || 'N/A')
                        )}
                      </div>
                      <div className="critical-identity-info">
                        <div className="critical-identity-name">
                          {item?.name || item?.displayName || 'Unnamed'}
                        </div>
                        <div className="critical-identity-secondary">
                          {item?.email || item?.upn || item?.type || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* ── Type ──────────────────────────── */}
                  <td className="td-type">
                    <span className={`badge ${item?.type === 'Application' || item?.type === 'ServicePrincipal' ? 'badge-nhi' : 'badge-human'}`}>
                      {formatType(item?.type || 'Unknown')}
                    </span>
                  </td>
                  {/* ── Department ─────────────────────── */}
                  <td className="td-department">
                    <span className="cell-text">{item?.department || 'N/A'}</span>
                  </td>
                  {/* ── Status ───────────────────────── */}
                  <td className="td-status">
                    <span className="cell-text">{item?.status || 'unknown'}</span>
                  </td>
                  {/* ── Risk ──────────────────────────── */}
                  <td className="td-risk">
                    <Badge variant={getRiskClassName(risk?.stage)}>
                      {risk?.stage}
                    </Badge>
                  </td>
                  {/* ── Score ─────────────────────────── */}
                  <td className="td-score">
                    <span className="critical-score-value">{formatNumber(risk?.score)}</span>
                  </td>
                  {/* ── Owner ─────────────────────────── */}
                  <td className="td-owner">
                    <span className="cell-text">
                      {typeof item?.owner === 'object'
                        ? item?.owner?.name || item?.owner?.email || 'N/A'
                        : item?.owner || 'N/A'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div className="table-pagination-footer">
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalCount}
            itemsPerPage={pagination.pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </div>
  );
};
export default CriticalRiskIdentitiesTable;